import React from 'react'

// Inline paywall card shown after the first (teaser) card on tool pages for
// free users. Ported from the mobile app's PreviewWall. Gates its CTA on trial
// status: never trialed → "Start Your Free Trial"; already trialed → "Upgrade".
export default function PreviewWall({ title, features, planName, accent = '#3b82f6', hadTrial = false }) {
  const trial = !hadTrial
  const goToPricing = () => { window.location.hash = '#/pricing' }

  return (
    <div style={{
      margin: '0 12px 12px',
      borderRadius: '20px',
      boxShadow: `0 8px 24px ${accent}40`,
      // Gradient border via padding trick
      padding: '1.5px',
      background: `linear-gradient(135deg, ${accent}cc 0%, ${accent}44 100%)`
    }}>
      <style>{`
        @keyframes previewWallRing {
          0%   { transform: scale(1);    opacity: 0.5; }
          100% { transform: scale(1.35); opacity: 0;   }
        }
      `}</style>
      <div style={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '18px',
        background: '#0e1626',
        padding: '24px 20px'
      }}>
        {/* Glow blob top-right */}
        <div style={{
          position: 'absolute', top: '-60px', right: '-40px',
          width: '200px', height: '200px', borderRadius: '100px',
          background: `radial-gradient(circle, ${accent}40 0%, transparent 70%)`,
          pointerEvents: 'none'
        }} />

        {/* Lock badge with pulsing ring */}
        <div style={{ position: 'relative', width: '76px', height: '76px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{
            position: 'absolute',
            width: '60px', height: '60px',
            borderRadius: '50%',
            border: `2px solid ${accent}`,
            animation: 'previewWallRing 1.5s ease-in-out infinite',
            pointerEvents: 'none'
          }} />
          <div style={{
            width: '58px', height: '58px',
            borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: `${accent}22`,
            border: `1.5px solid ${accent}88`
          }}>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          </div>
        </div>

        {/* Plan tag */}
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
          <span style={{
            display: 'inline-block',
            borderRadius: '999px',
            padding: '2px 10px',
            background: `${accent}22`,
            fontSize: '9px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.06em',
            color: accent
          }}>
            {planName}
          </span>
        </div>

        <h3 style={{ margin: '8px 0 0', textAlign: 'center', fontSize: '17px', fontWeight: '800', color: '#ffffff' }}>
          {title}
        </h3>

        {/* Features */}
        <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '8px', padding: '0 4px' }}>
          {features.map(f => (
            <div key={f} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{
                width: '16px', height: '16px', minWidth: '16px',
                borderRadius: '50%',
                background: 'rgba(52,211,153,0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </span>
              <span style={{ flex: 1, fontSize: '13px', color: '#cbd5e1' }}>{f}</span>
            </div>
          ))}
        </div>

        {/* CTA */}
        <button
          onClick={goToPricing}
          style={{
            marginTop: '20px',
            width: '100%',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
            padding: '14px 0',
            borderRadius: '12px',
            border: 'none',
            cursor: 'pointer',
            background: `linear-gradient(135deg, ${accent} 0%, ${accent}cc 100%)`,
            color: '#ffffff',
            fontSize: '15px', fontWeight: '800',
            fontFamily: 'inherit'
          }}
        >
          {trial ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/>
              <path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/>
              <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/>
              <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/>
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
            </svg>
          )}
          {trial ? 'Start Your Free Trial' : `Upgrade to ${planName}`}
        </button>

        {trial && (
          <p style={{ margin: '10px 0 0', textAlign: 'center', fontSize: '12px', color: '#64748b' }}>
            7 days free · Cancel anytime
          </p>
        )}
      </div>
    </div>
  )
}
